import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { StudentNav } from '../../components/shared/BottomNav';
import {
  subscribeToWeeks,
  subscribeToActiveGoal,
  subscribeToGoalHistory,
  cancelGoal,
  markGoalPurchased,
  formatCurrency,
  weeksUntilTarget,
} from '../../services/firebase';

const StudentSavings = () => {
  const { family } = useAuth();
  const navigate = useNavigate();
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeGoal, setActiveGoal] = useState(null);
  const [goalLoading, setGoalLoading] = useState(true);
  const [goalHistory, setGoalHistory] = useState([]);

  const matchConfig = family?.match;
  const matchEnabled = matchConfig?.enabled;

  useEffect(() => {
    if (!family?.id) return;
    const unsub = subscribeToWeeks(family.id, (w) => {
      setWeeks(w);
      setLoading(false);
    });
    return unsub;
  }, [family?.id]);

  useEffect(() => {
    if (!family?.id) return;
    const unsub = subscribeToActiveGoal(family.id, (goal) => {
      setActiveGoal(goal);
      setGoalLoading(false);
    });
    return unsub;
  }, [family?.id]);

  useEffect(() => {
    if (!family?.id) return;
    const unsub = subscribeToGoalHistory(family.id, setGoalHistory);
    return unsub;
  }, [family?.id]);

  const closedWeeks = weeks.filter(w => w.closedOut);
  const totalSaved = weeks.reduce((s, w) => s + (w.saved || 0), 0);
  const totalMatch = weeks.reduce((s, w) => s + (w.matchEarned || 0), 0);
  const totalEarnings = totalSaved + totalMatch;
  const avgSaved = closedWeeks.length > 0 ? totalSaved / closedWeeks.length : 0;
  const bestWeek = closedWeeks.reduce((best, w) =>
    (w.saved || 0) > (best?.saved || 0) ? w : best, null);

  // Completed goals the student has confirmed as purchased — deduct from net balance
  const purchasedGoalsTotal = goalHistory
    .filter(g => g.status === 'completed' && g.spentFromSavings)
    .reduce((s, g) => s + (g.targetAmount || 0), 0);

  // Net savings = what they've saved minus what they've spent on purchased goals
  const netSavings = Math.max(0, Math.round((totalSaved - purchasedGoalsTotal) * 100) / 100);
  const hasPurchasedGoals = purchasedGoalsTotal > 0;

  // Completed goals awaiting "Did you buy it?" confirmation
  const pendingPurchaseGoals = goalHistory.filter(
    g => g.status === 'completed' && !g.purchasedAt && !g.spentFromSavings
  );

  return (
    <div className="screen">
      <StudentNav />
      <div className="page-header">
        <h1 className="page-title">Savings</h1>
      </div>

      {/* Total saved hero */}
      <div className="card--hero animate-in" style={{ marginBottom: 20, textAlign: 'center' }}>
        <p style={{ opacity: 0.75, fontSize: '0.8125rem', marginBottom: 8 }}>
          {hasPurchasedGoals ? 'Current savings balance' : 'Total saved to date'}
        </p>
        <div className="amount-large" style={{ marginBottom: (matchEnabled || hasPurchasedGoals) ? 16 : 0 }}>
          {formatCurrency(hasPurchasedGoals ? netSavings : totalSaved)}
        </div>
        {hasPurchasedGoals && (
          <>
            <div style={{ height: 1, background: 'rgba(255,255,255,0.2)', margin: '0 0 12px' }} />
            <div style={{ display: 'flex', justifyContent: 'space-around', marginBottom: matchEnabled && totalMatch > 0 ? 12 : 0 }}>
              <div>
                <div style={{ opacity: 0.7, fontSize: '0.75rem', marginBottom: 4 }}>Total saved</div>
                <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{formatCurrency(totalSaved)}</div>
              </div>
              <div style={{ width: 1, background: 'rgba(255,255,255,0.2)' }} />
              <div>
                <div style={{ opacity: 0.7, fontSize: '0.75rem', marginBottom: 4 }}>Spent on goals</div>
                <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>−{formatCurrency(purchasedGoalsTotal)}</div>
              </div>
              <div style={{ width: 1, background: 'rgba(255,255,255,0.2)' }} />
              <div>
                <div style={{ opacity: 0.7, fontSize: '0.75rem', marginBottom: 4 }}>Balance</div>
                <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{formatCurrency(netSavings)}</div>
              </div>
            </div>
          </>
        )}
        {matchEnabled && totalMatch > 0 && (
          <>
            <div style={{ height: 1, background: 'rgba(255,255,255,0.2)', margin: '16px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-around' }}>
              <div>
                <div style={{ opacity: 0.7, fontSize: '0.75rem', marginBottom: 4 }}>Your savings</div>
                <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{formatCurrency(totalSaved)}</div>
              </div>
              <div style={{ width: 1, background: 'rgba(255,255,255,0.2)' }} />
              <div>
                <div style={{ opacity: 0.7, fontSize: '0.75rem', marginBottom: 4 }}>Parent match</div>
                <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{formatCurrency(totalMatch)}</div>
              </div>
              <div style={{ width: 1, background: 'rgba(255,255,255,0.2)' }} />
              <div>
                <div style={{ opacity: 0.7, fontSize: '0.75rem', marginBottom: 4 }}>Total</div>
                <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{formatCurrency(totalEarnings)}</div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 24 }}>
        <StatCard label="Weeks saved" value={closedWeeks.filter(w => w.saved > 0).length} />
        <StatCard label="Avg / week" value={formatCurrency(avgSaved)} />
        <StatCard label="Best week" value={bestWeek ? formatCurrency(bestWeek.saved) : '$0'} />
      </div>

      {/* Savings goal card */}
      {!goalLoading && (
        activeGoal ? (
          <GoalCard
            goal={activeGoal}
            familyId={family?.id}
            onEdit={() => navigate('/savings/goal/new', { state: { goal: activeGoal } })}
            onCancel={async () => { await cancelGoal(family.id); }}
          />
        ) : (
          <div className="card" style={{ marginBottom: 24, display: 'flex',
            alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 2 }}>No savings goal set</div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-2)' }}>
                Set a goal to track what you're saving toward.
              </div>
            </div>
            <button className="btn btn--primary" style={{ fontSize: '0.8125rem', padding: '8px 16px',
              flexShrink: 0 }}
              onClick={() => navigate('/savings/goal/new')}>
              Set goal
            </button>
          </div>
        )
      )}

      {/* "Did you buy it?" prompt for completed goals awaiting confirmation */}
      {pendingPurchaseGoals.map(goal => (
        <PurchaseConfirmCard
          key={goal.name + (goal.completedAt?.seconds || '')}
          goal={goal}
          onConfirm={async () => {
            await markGoalPurchased(family.id, goal.name);
          }}
        />
      ))}

      {/* Completed goals history */}
      {goalHistory.filter(g => g.status === 'completed' && g.purchasedAt).length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ marginBottom: 12, fontSize: '1rem' }}>Completed goals</h3>
          <div className="stack stack-3">
            {goalHistory
              .filter(g => g.status === 'completed' && g.purchasedAt)
              .map((g, i) => (
                <CompletedGoalRow key={g.name + i} goal={g} />
              ))}
          </div>
        </div>
      )}

      {/* Match explanation */}
      {matchEnabled && (
        <div className="card--accent animate-in" style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <span style={{ fontSize: 24 }}>🎯</span>
            <div>
              <div style={{ fontWeight: 700, color: 'var(--color-primary)', marginBottom: 4 }}>
                Your savings match
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--color-text-2)', lineHeight: 1.5 }}>
                {matchConfig.type === 'percentage'
                  ? `For every dollar you save, your parent adds ${matchConfig.value} cents.`
                  : `For every dollar you save, your parent adds $${matchConfig.value}.`}
                {matchConfig.monthlyCap > 0 && ` Capped at ${formatCurrency(matchConfig.monthlyCap)} per month.`}
                {' '}Close out each week to lock in your match!
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Week history */}
      <h3 style={{ marginBottom: 16 }}>Week by week</h3>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <div className="spinner" />
        </div>
      ) : closedWeeks.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 48 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🌱</div>
          <p style={{ color: 'var(--color-text-2)', lineHeight: 1.6 }}>
            No closed weeks yet. Close out your first week to start building your savings history.
          </p>
        </div>
      ) : (
        <div className="stack stack-3 animate-in">
          {closedWeeks.map((week, i) => (
            <SavingsWeekRow key={week.id} week={week} matchEnabled={matchEnabled} rank={i}
              goalAllocation={week.goalAllocation || 0}
              goalName={activeGoal?.name || ''}
              goalHistory={goalHistory} />
          ))}
        </div>
      )}
    </div>
  );
};

const SavingsWeekRow = ({ week, matchEnabled, rank, goalAllocation, goalName, goalHistory }) => {
  const saved = week.saved || 0;
  const match = week.matchEarned || 0;

  return (
    <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 14,
      animation: `fadeIn 0.3s ease ${rank * 50}ms both` }}>
      <div style={{ width: 44, height: 44, borderRadius: 12,
        background: saved > 0 ? 'var(--color-primary-pale)' : 'var(--color-surface-2)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 22, flexShrink: 0 }}>
        {saved > 0 ? '✅' : '—'}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: '0.9375rem' }}>
          {(() => {
            const start = week.startDate?.toDate ? week.startDate.toDate()
              : week.startDate ? new Date(week.startDate) : null;
            if (!start) return week.id;
            const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
            return `${start.getMonth() + 1}/${start.getDate()}–${end.getMonth() + 1}/${end.getDate()}`;
          })()}
        </div>
        {matchEnabled && match > 0 && (
          <div style={{ fontSize: '0.75rem', color: 'var(--color-text-3)', marginTop: 2 }}>
            + {formatCurrency(match)} match
          </div>
        )}
        {goalAllocation > 0 && (() => {
          // Priority: week's own goalName field (stamped at close-out) →
          // completed goal in history → generic label
          const weekGoalName = week.goalName || goalName;
          const completedGoal = !weekGoalName && goalHistory?.find(
            g => g.status === 'completed'
          );
          const chipLabel = weekGoalName || (completedGoal ? completedGoal.name : 'savings goal');
          return (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
              background: 'var(--color-primary-pale)', borderRadius: 'var(--radius-full)',
              padding: '2px 8px', marginTop: 4 }}>
              <div style={{ width: 5, height: 5, borderRadius: '50%',
                background: 'var(--color-primary)', flexShrink: 0 }} />
              <span style={{ fontSize: '0.6875rem', color: 'var(--color-primary)',
                fontWeight: 600 }}>
                {formatCurrency(goalAllocation)} toward {chipLabel}
              </span>
            </div>
          );
        })()}
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontWeight: 700, color: saved > 0 ? 'var(--color-primary)' : 'var(--color-text-3)',
          fontFamily: 'var(--font-display)', fontSize: '1.1rem' }}>
          {formatCurrency(saved)}
        </div>
        {matchEnabled && match > 0 && (
          <div style={{ fontSize: '0.75rem', color: 'var(--color-primary)', fontWeight: 600, marginTop: 2 }}>
            {formatCurrency(saved + match)} total
          </div>
        )}
      </div>
    </div>
  );
};


const GoalCard = ({ goal, familyId, onCancel, onEdit }) => {

  const pct = Math.min(100, Math.round((goal.amountSaved / goal.targetAmount) * 100));
  const remaining = Math.max(0, Math.round((goal.targetAmount - goal.amountSaved) * 100) / 100);

  const targetDate = goal.targetDate?.toDate
    ? goal.targetDate.toDate()
    : goal.targetDate instanceof Date
      ? goal.targetDate
      : goal.targetDate?.seconds
        ? new Date(goal.targetDate.seconds * 1000)
        : null;

  const weeksLeft = weeksUntilTarget(targetDate);

  const dateStr = targetDate
    ? targetDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '—';

  // Always compute weekly target from remaining balance and weeks left —
  // the stored goal.weeklyTarget can be stale if the goal predates the
  // remaining-balance fix or hasn't been re-saved via the edit screen.
  const effectiveWeeklyTarget = weeksLeft && weeksLeft > 0 && remaining > 0
    ? Math.round(Math.ceil((remaining / weeksLeft) * 100) / 100 * 100) / 100
    : goal.weeklyTarget;

  // On pace if savings-so-far meets or exceeds the minimum steady-savings trajectory.
  // Uses >= (not >) so being exactly on track counts as on pace — this also handles a
  // brand-new goal ($0 saved, full timeline ahead), which lands exactly on the boundary.
  // The EPSILON absorbs floating-point rounding: e.g. 60/200 = 0.3 but 1 - 140/200 =
  // 0.30000000000000004, so a bare >= wrongly reads "exactly on pace" as behind. Comparing
  // against (threshold - EPSILON) treats values that are equal within rounding error as on pace.
  const PACE_EPSILON = 1e-9;
  const savedRatio = goal.amountSaved / goal.targetAmount;
  const paceThreshold = 1 - (weeksLeft * effectiveWeeklyTarget / goal.targetAmount);
  const isAhead = effectiveWeeklyTarget > 0 && weeksLeft !== null &&
    savedRatio >= paceThreshold - PACE_EPSILON;

  return (
    <div className="card" style={{ marginBottom: 24, padding: 0, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '14px 16px 12px', display: 'flex',
        justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12,
            background: 'var(--color-primary-pale)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
            ⭐
          </div>
          <div>
            <div style={{ fontSize: '0.6875rem', fontWeight: 600,
              color: 'var(--color-text-3)', textTransform: 'uppercase',
              letterSpacing: '0.05em', marginBottom: 2 }}>
              Your savings goal
            </div>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--color-text)' }}>
              {goal.name}
            </div>
          </div>
        </div>
        <button onClick={onEdit}
          style={{ background: 'none', border: '0.5px solid var(--color-border)',
            borderRadius: 'var(--radius-full)', padding: '4px 12px',
            fontSize: '0.75rem', color: 'var(--color-text-3)',
            cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
          Edit
        </button>
      </div>

      {/* Progress */}
      <div style={{ padding: '0 16px 14px' }}>
        <div style={{ marginBottom: 8 }}>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.375rem',
            fontWeight: 700, color: 'var(--color-primary)' }}>
            {formatCurrency(goal.amountSaved)}
          </span>
          <span style={{ fontSize: '0.875rem', color: 'var(--color-text-3)',
            marginLeft: 6 }}>
            reserved for {formatCurrency(goal.targetAmount)} goal
          </span>
        </div>
        <div className="progress-bar" style={{ marginBottom: 6 }}>
          <div className="progress-bar__fill" style={{ width: `${pct}%` }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--color-text-3)' }}>
            {pct}% · {formatCurrency(remaining)} to go
          </span>
          <span style={{ fontSize: '0.75rem', color: 'var(--color-primary)', fontWeight: 600 }}>
            Target: {dateStr}
          </span>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 8, padding: '0 16px 14px' }}>
        {[
          { label: 'Target date', value: dateStr },
          { label: 'Weeks left', value: weeksLeft ?? '—' },
          { label: 'Save/week', value: formatCurrency(effectiveWeeklyTarget), green: true },
        ].map(({ label, value, green }) => (
          <div key={label} style={{ background: 'var(--color-surface-2)',
            borderRadius: 'var(--radius-sm)', padding: '8px 10px', textAlign: 'center' }}>
            <div style={{ fontWeight: 700, fontSize: '0.875rem',
              color: green ? 'var(--color-primary)' : 'var(--color-text)' }}>
              {value}
            </div>
            <div style={{ fontSize: '0.6875rem', color: 'var(--color-text-3)', marginTop: 2 }}>
              {label}
            </div>
          </div>
        ))}
      </div>

      {/* Pace callout */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8,
        background: 'var(--color-primary-pale)',
        padding: '10px 16px', borderTop: '1px solid var(--color-border)' }}>
        <div style={{ width: 7, height: 7, borderRadius: '50%',
          background: 'var(--color-primary)', flexShrink: 0, marginTop: 4 }} />
        <div style={{ fontSize: '0.8125rem', color: 'var(--color-primary)', lineHeight: 1.5 }}>
          {isAhead
            ? <><strong>On pace.</strong> Save {formatCurrency(effectiveWeeklyTarget)} at close-out this week to stay on track.</>
            : <><strong>Behind pace.</strong> Save {formatCurrency(effectiveWeeklyTarget)} this week to catch up.</>
          }
        </div>
      </div>
    </div>
  );
};


const PurchaseConfirmCard = ({ goal, onConfirm }) => {
  const [confirming, setConfirming] = useState(false);
  const [loading, setLoading] = useState(false);

  const completedDate = goal.completedAt?.toDate
    ? goal.completedAt.toDate()
    : goal.completedAt instanceof Date
      ? goal.completedAt
      : goal.completedAt?.seconds
        ? new Date(goal.completedAt.seconds * 1000)
        : null;

  const dateStr = completedDate
    ? completedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';

  return (
    <div className="card--accent animate-in" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <span style={{ fontSize: 24, flexShrink: 0 }}>🎉</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, color: 'var(--color-primary)',
            marginBottom: 4, fontSize: '0.9375rem' }}>
            You hit your {goal.name} goal!
          </div>
          <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-2)',
            lineHeight: 1.5, marginBottom: 12 }}>
            {formatCurrency(goal.targetAmount)} saved{dateStr ? ` by ${dateStr}` : ''}.
            Did you buy it?
          </div>
          {!confirming ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="btn btn--primary"
                style={{ fontSize: '0.8125rem', padding: '8px 16px' }}
                onClick={() => setConfirming(true)}>
                Yes, I bought it ✓
              </button>
              <button
                className="btn btn--secondary"
                style={{ fontSize: '0.8125rem', padding: '8px 16px' }}
                onClick={() => setConfirming(true)}>
                Not yet
              </button>
            </div>
          ) : (
            <div className="animate-in">
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-2)',
                marginBottom: 10, lineHeight: 1.5 }}>
                Marking as purchased will deduct{' '}
                <strong>{formatCurrency(goal.targetAmount)}</strong> from your
                savings balance. This can't be undone.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="btn btn--primary"
                  style={{ fontSize: '0.8125rem', padding: '8px 16px' }}
                  disabled={loading}
                  onClick={async () => {
                    setLoading(true);
                    await onConfirm();
                  }}>
                  {loading
                    ? <span className="spinner spinner--white"
                        style={{ width: 16, height: 16 }} />
                    : 'Confirm purchase'}
                </button>
                <button
                  className="btn btn--secondary"
                  style={{ fontSize: '0.8125rem', padding: '8px 16px' }}
                  onClick={() => setConfirming(false)}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CompletedGoalRow = ({ goal }) => {
  const purchasedDate = goal.purchasedAt?.toDate
    ? goal.purchasedAt.toDate()
    : goal.purchasedAt instanceof Date
      ? goal.purchasedAt
      : goal.purchasedAt?.seconds
        ? new Date(goal.purchasedAt.seconds * 1000)
        : null;

  const dateStr = purchasedDate
    ? purchasedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '';

  return (
    <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <div style={{ width: 44, height: 44, borderRadius: 12,
        background: 'var(--color-primary-pale)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
        ✅
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: '0.9375rem' }}>{goal.name}</div>
        {dateStr && (
          <div style={{ fontSize: '0.75rem', color: 'var(--color-text-3)', marginTop: 2 }}>
            Purchased {dateStr}
          </div>
        )}
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontWeight: 700, color: 'var(--color-primary)',
          fontFamily: 'var(--font-display)', fontSize: '1.1rem' }}>
          {formatCurrency(goal.targetAmount)}
        </div>
        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-3)', marginTop: 2 }}>
          spent from savings
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value }) => (
  <div className="card animate-in" style={{ textAlign: 'center', padding: '14px 10px' }}>
    <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 700 }}>{value}</div>
    <div style={{ fontSize: '0.7rem', color: 'var(--color-text-3)', marginTop: 4 }}>{label}</div>
  </div>
);

export default StudentSavings;
